import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { NavigationService } from '@treasury/core/navigation';
import { RowActionCommandResult } from '@treasury/domain-components/commands/users/base.cmd';
import { CompanyUserActionService } from '@treasury/domain-components/commands/users/company-users/user-actions.service';
import { CompanyUserActionType } from '@treasury/domain-components/commands/users/company-users/user-actions.types';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import { CompanyService } from '@treasury/domain/backoffice/services/company';
import { CompanySelectionService } from '@treasury/domain/backoffice/services/company-selection/company-selection.service';
import { CompanyUserService, CompanyUserSummary } from '@treasury/domain/company/user';
import { TmBaseComponent } from '@treasury/presentation';
import {
    TableClickPayload,
    TmTableOptions,
} from '@treasury/presentation/components/jha-wc/tm-table';
import { TmSideDrawerService } from '@treasury/presentation/services/side-drawer';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { UserSettingsCommand } from '../../../commands/company/userSettings/userSettings.cmd';
import { BoViewUserCommand, ConsumerViewUserCommand } from '../../../commands/users/company-users';
import { companyUserListTagName as tagName } from '../../company-users.types';
import '../../overlays/drawers/user-status-confirmation';
import { CompanyUserViewModel } from './company-user.vm';
import {
    createAllCompanyUsersView,
    createEnrolledCompanyUsersView,
    createPendingCompanyUsersView,
} from './company-users-table-views';
import { RowActionVm } from './row-action.vm';

import '@treasury/presentation/components/jh-ui';
// eslint-disable-next-line no-duplicate-imports
import '@banno/jha-wc/src/forms/jha-form-switch/jha-form-switch';
import '@banno/jha-wc/src/tables/advanced/jha-advanced-table/jha-advanced-table';
import '@jack-henry/jh-icons/icons-wc/icon-chevron-down-small.js';
import '@jack-henry/jh-icons/icons-wc/icon-gear.js';
import '@jack-henry/jh-icons/icons-wc/icon-plus.js';
import '@jack-henry/jh-ui/components/button/button.js';
import '@jack-henry/jh-ui/components/input/input.js';
import '@jack-henry/jh-ui/components/notification/notification.js';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/jha-wc/tm-table';

type RowActionEvent = CustomEvent<{ item: CompanyUserSummary; action: string }>;

@customElement(tagName)
export class CompanyUserList extends TmBaseComponent {
    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly companyService: CompanyService;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly sideDrawerService: TmSideDrawerService;

    @InjectProperty()
    declare private readonly navigationService: NavigationService;

    @InjectProperty({ async: true, token: CompanyUserActionService })
    declare private readonly userActionsServicePromise: Promise<CompanyUserActionService>;

    @InjectProperty()
    declare private readonly companySelectionService: CompanySelectionService;

    @InjectProperty()
    declare private readonly userActionsService: CompanyUserActionService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @InjectProperty()
    declare private readonly userSettingsCommand: UserSettingsCommand;

    @InjectProperty()
    declare private readonly boViewUserCommand: BoViewUserCommand;

    @InjectProperty()
    declare private readonly consumerViewUserCommand: ConsumerViewUserCommand;

    @DeepReactive()
    private company?: CompanyViewModel;

    @state()
    private users: CompanyUserViewModel[] = [];

    @state()
    private loading = false;

    private get isBoApp() {
        return this.config.app === AppType.BackOffice;
    }

    private get isConsumerApp() {
        return this.config.app === AppType.Channel || this.config.app === AppType.Pwa;
    }

    private get options(): TmTableOptions<CompanyUserViewModel> {
        const view = createAllCompanyUsersView(
            this.rowActions,
            e => this.handleRowAction(e),
            async () => await this.getUsers(),
            () => this.handleSettingsClick(),
            () => this.handleAddUser(),
            e => this.handleRowClick(e),
            this.config.app
        );
        return {
            ...view,
            refreshData: () => this.getUsers(),
            displayAllView: false,
        };
    }

    public render() {
        return html`<tm-jh-card
            padding="none"
            class=${classMap({
                'mr-8': this.isBoApp,
            })}
        >
            ${this.renderContent()}
        </tm-jh-card>`;
    }

    protected async firstUpdated() {
        this.loading = true;
        try {
            if (this.isBoApp) {
                await this.companySelectionService.companyChange$.toPromise();

                this.company = this.companySelectionService.company;
                if (!this.company?.id) {
                    throw new Error('Failed to get company users: Company ID is invalid.');
                }
            }
            if (this.isConsumerApp) {
                await this.userActionsService.loadCompanyUserActions();
            }
            this.users = await this.getUsers();
        } catch (e) {
            throw new Error(`Error loading company info: ${e}`);
        } finally {
            this.loading = false;
        }
    }

    private renderContent() {
        if (this.company && this.company.notOnboarded) {
            return html`<tm-jh-card class="m-8">
                <h2>This company is not yet onboarded</h2>
            </tm-jh-card>`;
        }

        return html`
            <tm-table
                header="Users"
                .loading=${this.loading}
                .options=${this.options}
                .items=${this.users}
            >
            </tm-table>
        `;
    }

    private async getUsers() {
        if (this.isBoApp && !this.company?.id) {
            this.toastService.show(
                'Company ID is required to refresh the users',
                TmToastPosture.Danger
            );
            return [];
        }
        let users: CompanyUserViewModel[] = [];
        try {
            const response = await this.companyUserService.getAllCompanyUsers(this.company?.id);
            users = response.map(user => new CompanyUserViewModel(user));
        } catch (e) {
            throw new Error(`Error loading company users: ${e}`);
        } finally {
            this.loading = false;
        }

        return users;
    }

    private get views() {
        return [
            createEnrolledCompanyUsersView(
                this.rowActions,
                e => this.handleRowAction(e),
                async () => (await this.getUsers()).filter(user => user.isEnrolledValue),
                () => this.handleSettingsClick(),
                () => this.handleAddUser(),
                e => this.handleRowClick(e)
            ),
            createPendingCompanyUsersView(
                this.rowActions,
                e => this.handleRowAction(e),
                async () => (await this.getUsers()).filter(user => user.isPendingValue),
                () => this.handleSettingsClick(),
                () => this.handleAddUser(),
                e => this.handleRowClick(e)
            ),
        ];
    }

    private get rowActions() {
        if (!this.userActionsService) {
            return [];
        }

        return this.userActionsService.userActions.map(
            (action: RowActionCommandResult<CompanyUserSummary, CompanyUserActionType>) => {
                return new RowActionVm<CompanyUserSummary>(
                    action.type,
                    action.title,
                    action.hiddenCondition,
                    action.disabledCondition
                );
            }
        );
    }

    private handleRowClick(data: TableClickPayload<CompanyUserViewModel>) {
        if (this.isBoApp && !this.company) {
            this.toastService.show(
                'Company was not found when attempting to load the user',
                TmToastPosture.Danger,
                true
            );
            return;
        }
        if (this.isBoApp && !!this.company) {
            const { id } = data.item;
            if (!id) {
                this.toastService.show(
                    'User ID is required to view user.',
                    TmToastPosture.Danger,
                    true
                );
                return;
            }
            this.boViewUserCommand.command(this.company.id).action(id);
        } else {
            this.consumerViewUserCommand.command().action(data.item);
        }
    }

    private handleSettingsClick() {
        if (!this.company?.id) {
            return;
        }
        this.userSettingsCommand.command().action(this.company?.id);
    }

    private handleStatusToggle(value: boolean, item: CompanyUserSummary) {
        if (this.isBoApp && !exists(this.company?.id)) {
            return;
        }

        this.sideDrawerService.open(
            html`<user-status-confirmation
                @close=${() => {
                    this.sideDrawerService.close();
                }}
                @confirm=${async (e: CustomEvent) => {
                    if (!item.id || !this.company?.id) {
                        throw new Error('Failed to save user status change');
                    }
                    const comments = e.detail;

                    const response = await this.companyUserService.setActive(
                        item.id,
                        value, // endpoint wants current value, not desired state
                        comments,
                        this.company?.id
                    );
                    const user = new CompanyUserViewModel(response);
                    this.users = this.users.map(u => (u.id === user.id ? user : u));
                    this.sideDrawerService.close();
                }}
            ></user-status-confirmation>`,
            'Confirm User Status Change'
        );
    }

    private handleAddUser() {
        this.navigationService.navigate(`/company/${this.company?.id}/users/create`);
    }

    private handleRowAction(e: RowActionEvent) {
        if (!this.userActionsService) {
            throw new Error('User actions service is not defined');
        }
        const { item, action } = e.detail as { item: CompanyUserSummary; action: string };

        if (action === CompanyUserActionType.View) {
            this.handleViewUserClick(item);
        }

        this.userActionsService.handleActionClick(action as CompanyUserActionType, item);
    }

    private async handleViewUserClick(user: CompanyUserSummary) {
        if (!user.id || !user.userId || !user.userName) {
            throw new Error('User not found');
        }
        if (this.isBoApp && !this.company?.id) {
            throw new Error('Company ID is required');
        }
        this.companyUserService.saveUserToRecentlyViewedUser(user.id, this.company?.id);
    }

    public static get styles() {
        return css`
            :host {
                --jha-table-row-cursor: pointer;
                --tm-table-chrome-padding-x: 8px;
            }
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CompanyUserList;
    }
}
